/** Pure assembly/publication contract shared with the React editor. No raw source
 * document is spread into a draft or a public snapshot. */
export const ITEM_FIELDS = [
  'title',
  'pieceType',
  'creator',
  'description',
  'credits',
  'biography',
  'durationSeconds',
  'image',
  'awardEligible',
] as const
export type ItemField = (typeof ITEM_FIELDS)[number]
export const PIECE_TYPES = ['film', 'live', 'trailer', 'introduction', 'intermission'] as const
export type PieceType = (typeof PIECE_TYPES)[number]
export interface ProgramImage {
  mediaId: string
  url: string
  alt: string
  width: number
  height: number
  focusX?: number
  focusY?: number
}
export interface PieceValues {
  title?: string
  pieceType?: PieceType
  creator?: string
  description?: string
  credits?: string
  biography?: string
  durationSeconds?: number | null
  image?: ProgramImage | null
  awardEligible?: boolean
}
export interface ProgramItem {
  id: string
  submissionId?: string
  origin: 'submission' | 'manual'
  source: PieceValues
  overrides: PieceValues
  approvedFields: ItemField[]
  excluded?: boolean
  inactiveSource?: boolean
}
export interface FieldMapping {
  source: 'intake' | 'technical'
  fieldId?: string
  libraryId?: string
  unit?: 'seconds' | 'minutes'
}
export type FieldMappings = Partial<
  Record<Exclude<ItemField, 'image' | 'awardEligible' | 'pieceType'>, FieldMapping>
>
export interface ProgramSettings {
  mappings: FieldMappings
  defaultPieceType: PieceType
  participantIds: string[] | null
  images: Record<string, { mediaId: string; alt: string }>
  branding: {
    accent: string
    heroMediaId?: string
    logoMediaId?: string
    heroFocusX?: number
    heroFocusY?: number
  }
  showOverrides: Partial<ShowValues>
  showApproved: boolean
  votingEnabled: boolean
}
export interface ShowValues {
  title: string
  startsAt: string
  venue: string
  description: string
  timeZone: string
}
export interface ProgramDraft {
  id: string
  slug: string
  productionId: string
  eventId: string
  revision: number
  settings: ProgramSettings
  showSource: ShowValues
  items: ProgramItem[]
  warnings: ProgramWarning[]
  branding: PublicProgram['branding']
  publishedDigest?: string
  approvedBranding?: PublicProgram['branding']
}
export interface ProgramWarning {
  itemId?: string
  field?: string
  message: string
  blocking: boolean
}
export interface PublicProgram {
  schemaVersion: 1
  id: string
  slug: string
  productionId: string
  eventId: string
  version: number
  show: ShowValues
  items: Array<PieceValues & { id: string }>
  branding: { accent: string; hero?: ProgramImage; logo?: ProgramImage }
  votingEnabled: boolean
}
export interface Answer {
  fieldLibraryId?: string
  value: unknown
}
export interface SourceSubmission {
  id: string
  applicationId?: string
  applicationType?: string
  productionId?: string
  intakeSubmissionId?: string
  status?: string
  confirmedAt?: unknown
  participantAcceptedAt?: unknown
  participantDeclinedAt?: unknown
  participantWithdrawnAt?: unknown
  participantOfferStatus?: string
  techSubmissionId?: string
  rank?: number
  answers?: Record<string, Answer>
}
export interface SourceMedia {
  id: string
  status?: string
  mimeType?: string
  url?: string
  width?: number
  height?: number
}
export interface AssemblySources {
  production: {
    id: string
    name: string
    intakeApplicationId?: string
    techApplicationId?: string
    participantPackage?: unknown
    performanceOptions?: Array<{ performanceId?: string; eventId?: string; startsAt?: string }>
  }
  event: {
    id: string
    productionId?: string
    title: string
    startsAt: string
    venue: string
    description?: string
    status?: string
  }
  privateFieldIds?: { intake: string[]; technical: string[] }
  performanceCount: number
  submissions: SourceSubmission[]
  media: SourceMedia[]
}
export function confirmedParticipant(s: SourceSubmission, packageRequired: boolean): boolean {
  if (
    s.status !== 'accepted' ||
    !s.confirmedAt ||
    s.participantDeclinedAt ||
    s.participantWithdrawnAt
  )
    return false
  if (s.participantOfferStatus)
    return s.participantOfferStatus === 'paid' && !!s.participantAcceptedAt
  return !packageRequired && !!s.participantAcceptedAt
}
export function effectiveValues(item: ProgramItem): PieceValues {
  return { ...item.source, ...item.overrides }
}
export function showValues(draft: ProgramDraft): ShowValues {
  return { ...draft.showSource, ...draft.settings.showOverrides }
}
export function resetOverride(item: ProgramItem, field: ItemField): ProgramItem {
  const overrides = { ...item.overrides }
  delete overrides[field]
  return { ...item, overrides, approvedFields: item.approvedFields.filter((f) => f !== field) }
}
export function safeImage(media: SourceMedia | undefined, alt: string): ProgramImage | undefined {
  if (
    !media ||
    media.status !== 'ready' ||
    !media.mimeType?.startsWith('image/') ||
    !media.url ||
    !alt.trim()
  )
    return undefined
  // Public processed media only; never applicant uploads or original file URLs.
  try {
    const url = new URL(media.url)
    const object = decodeURIComponent(url.pathname)
    if (
      url.protocol !== 'https:' ||
      url.hostname !== 'firebasestorage.googleapis.com' ||
      !/^\/v0\/b\/[^/]+\/o\/media\/processed\/[^/]+$/.test(object)
    )
      return undefined
  } catch {
    return undefined
  }
  if (
    !Number.isFinite(media.width) ||
    !Number.isFinite(media.height) ||
    (media.width ?? 0) <= 0 ||
    (media.height ?? 0) <= 0
  )
    return undefined
  return { mediaId: media.id, url: media.url, alt, width: media.width!, height: media.height! }
}
export function containsPrivateData(value: string): boolean {
  return (
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(value) ||
    /applicationSubmissions(?:\/|%2f)|[?&](?:responseToken|token|X-Goog-Signature|payment_intent_client_secret)=/i.test(
      value
    )
  )
}
export function mappedValue(
  answers: Record<string, Answer>,
  mapping: FieldMapping
): { value?: string | number; error?: string } {
  if (!!mapping.fieldId === !!mapping.libraryId)
    return { error: 'Select exactly one stable field ID or library ID.' }
  const matches = mapping.fieldId
    ? Object.entries(answers).filter(([id]) => id === mapping.fieldId)
    : Object.entries(answers).filter(([, a]) => a.fieldLibraryId === mapping.libraryId)
  if (matches.length !== 1)
    return {
      error: matches.length
        ? 'Library ID matches multiple answers. Select a field ID.'
        : 'Mapped answer is missing.',
    }
  const value = matches[0]![1].value
  if (typeof value === 'string') return { value }
  if (Array.isArray(value) && value.every((v) => typeof v === 'string'))
    return { value: value.join('\n') }
  if (typeof value === 'number' && Number.isFinite(value)) return { value }
  return { error: 'Mapped answer must be text, a text list, or a number.' }
}
export function assembleDraft(draft: ProgramDraft, sources: AssemblySources): ProgramDraft {
  const warnings: ProgramWarning[] = []
  const warn = (message: string, itemId?: string, field?: string, blocking = true) =>
    warnings.push({ message, ...(itemId ? { itemId } : {}), ...(field ? { field } : {}), blocking })
  if (sources.event.productionId !== sources.production.id)
    warn('The performance is no longer linked to this production.')
  const performanceOptions = (sources.production.performanceOptions ?? []).filter(
    (option) => option.eventId === sources.event.id
  )
  if (performanceOptions.length > 1)
    warn(
      'Multiple package performances reference this event. Give each performance a separate linked event before creating its program.'
    )
  if (
    performanceOptions.length === 1 &&
    performanceOptions[0]?.startsAt &&
    Date.parse(performanceOptions[0].startsAt) !== Date.parse(sources.event.startsAt)
  )
    warn(
      'The event date differs from its participant-package performance date. Correct the source schedule before publishing.'
    )
  if (sources.event.status === 'cancelled')
    warn('This performance is cancelled. Review before publishing.')
  if (sources.performanceCount > 1 && draft.settings.participantIds === null)
    warn(
      'Assign participants to this performance explicitly. Ticket selections do not define a lineup.'
    )
  for (const submission of sources.submissions)
    if (
      submission.applicationId === sources.production.intakeApplicationId &&
      !submission.productionId &&
      confirmedParticipant(submission, !!sources.production.participantPackage)
    )
      warn(
        'Confirmed submission is missing its production linkage. Repair the source record in the production workflow.',
        `submission-${submission.id}`,
        undefined,
        false
      )
  const confirmed = sources.submissions.filter(
    (s) =>
      s.productionId === draft.productionId &&
      s.applicationId === sources.production.intakeApplicationId &&
      s.applicationType !== 'tech_questionnaire' &&
      confirmedParticipant(s, !!sources.production.participantPackage)
  )
  const selected = confirmed.filter((s) =>
    draft.settings.participantIds === null
      ? sources.performanceCount === 1
      : draft.settings.participantIds.includes(s.id)
  )
  for (const id of draft.settings.participantIds ?? [])
    if (!confirmed.some((s) => s.id === id))
      warn(
        'Assigned participant is not currently confirmed; excluded from draft.',
        `submission-${id}`,
        undefined,
        false
      )
  selected.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0) || a.id.localeCompare(b.id))
  const imported: ProgramItem[] = selected.map((s) => {
    const id = `submission-${s.id}`
    const previous = draft.items.find((item) => item.id === id)
    const source: PieceValues = { pieceType: draft.settings.defaultPieceType, awardEligible: false }
    const tech = sources.submissions.find(
      (t) =>
        t.id === s.techSubmissionId &&
        t.intakeSubmissionId === s.id &&
        t.applicationId === sources.production.techApplicationId &&
        t.applicationType === 'tech_questionnaire'
    )
    for (const field of [
      'title',
      'creator',
      'description',
      'credits',
      'biography',
      'durationSeconds',
    ] as const) {
      const mapping = draft.settings.mappings[field]
      if (!mapping) {
        if (field === 'title' || field === 'credits')
          warn('Configure an explicit source field mapping or enter an override.', id, field)
        continue
      }
      const result = mappedValue(
        (mapping.source === 'technical' ? tech : s)?.answers ?? {},
        mapping
      )
      const sourceAnswers = (mapping.source === 'technical' ? tech : s)?.answers ?? {}
      const mappedIds = mapping.fieldId
        ? [mapping.fieldId]
        : Object.entries(sourceAnswers)
            .filter(([, a]) => a.fieldLibraryId === mapping.libraryId)
            .map(([id]) => id)
      if (
        mappedIds.some((fieldId) =>
          sources.privateFieldIds?.[
            mapping.source === 'technical' ? 'technical' : 'intake'
          ].includes(fieldId)
        )
      ) {
        warn(
          'Contact, payment, and upload fields cannot be mapped into public programs.',
          id,
          field
        )
        continue
      }
      if (result.error) {
        warn(result.error, id, field, field === 'title' || field === 'credits')
        continue
      }
      if (typeof result.value === 'string' && containsPrivateData(result.value)) {
        warn(
          'Mapped content contains contact information or a private file link. Supply a public override.',
          id,
          field
        )
        continue
      }
      if (field === 'durationSeconds') {
        const number =
          typeof result.value === 'number'
            ? result.value
            : /^\d+(\.\d+)?$/.test(result.value ?? '')
              ? Number(result.value)
              : NaN
        if (!mapping.unit || !Number.isFinite(number) || number < 0)
          warn(
            'Choose a duration unit and provide a numeric duration; no duration was inferred.',
            id,
            field,
            false
          )
        else source.durationSeconds = number * (mapping.unit === 'minutes' ? 60 : 1)
      } else source[field] = String(result.value ?? '')
    }
    const image = draft.settings.images[s.id]
    if (image) {
      const approved = safeImage(
        sources.media.find((m) => m.id === image.mediaId),
        image.alt
      )
      if (approved) source.image = approved
      else
        warn(
          'Selected image must be ready processed media with dimensions and alternative text.',
          id,
          'image'
        )
    }
    const approvedFields = (previous?.approvedFields ?? []).filter(
      (field) =>
        Object.prototype.hasOwnProperty.call(previous?.overrides ?? {}, field) ||
        JSON.stringify(previous?.source[field]) === JSON.stringify(source[field])
    )
    return {
      id,
      submissionId: s.id,
      origin: 'submission' as const,
      source,
      overrides: previous?.overrides ?? {},
      approvedFields,
      ...(previous?.excluded ? { excluded: true } : {}),
    }
  })
  // Retain removed source items privately, including their edits, so a return
  // to confirmed status restores edits and order. Never publish stale items.
  const activeIds = new Set(imported.map((i) => i.id))
  const ordered = draft.items.map((old) =>
    old.origin === 'manual'
      ? { ...old, source: { ...old.source }, approvedFields: [...old.approvedFields] }
      : (imported.find((i) => i.id === old.id) ?? { ...old, inactiveSource: true })
  )
  const items: ProgramItem[] = [
    ...ordered,
    ...imported.filter((i) => !draft.items.some((old) => old.id === i.id)),
  ]
  for (const item of items)
    if (item.origin === 'submission') item.inactiveSource = !activeIds.has(item.id)
  for (const item of items.filter((i) => i.origin === 'manual')) {
    const selectedImage = draft.settings.images[item.id]
    if (selectedImage) {
      const image = safeImage(
        sources.media.find((m) => m.id === selectedImage.mediaId),
        selectedImage.alt
      )
      if (!image) warn('Choose a ready processed image with alternative text.', item.id, 'image')
      if (JSON.stringify(item.source.image) !== JSON.stringify(image))
        item.approvedFields = item.approvedFields.filter((f) => f !== 'image')
      item.source = { ...item.source, image: image ?? null }
    }
  }
  const showSource: ShowValues = {
    title: sources.event.title,
    startsAt: sources.event.startsAt,
    venue: sources.event.venue,
    description: sources.event.description ?? '',
    timeZone: draft.showSource.timeZone || 'America/New_York',
  }
  const showApproved =
    draft.settings.showApproved && JSON.stringify(draft.showSource) === JSON.stringify(showSource)
  const branding: PublicProgram['branding'] = {
    ...draft.approvedBranding,
    accent: draft.settings.branding.accent,
  }
  for (const field of ['hero', 'logo'] as const) {
    const mediaId = draft.settings.branding[`${field}MediaId`]
    if (mediaId) {
      const image = safeImage(
        sources.media.find((m) => m.id === mediaId),
        field === 'hero' ? showValues({ ...draft, showSource }).title : 'Show logo'
      )
      if (image)
        branding[field] =
          field === 'hero'
            ? {
                ...image,
                focusX: draft.settings.branding.heroFocusX ?? 0.5,
                focusY: draft.settings.branding.heroFocusY ?? 0.5,
              }
            : image
      else warn(`Choose ready processed media for the ${field} image.`)
    }
  }
  return {
    ...draft,
    settings: { ...draft.settings, showApproved },
    showSource,
    branding,
    items,
    warnings,
  }
}
export function validateDraft(draft: ProgramDraft): ProgramWarning[] {
  const warnings = draft.warnings
    .map((w) => {
      const item = draft.items.find((i) => i.id === w.itemId)
      return item?.excluded || item?.inactiveSource ? { ...w, blocking: false } : w
    })
    .filter(
      (w) =>
        !w.field ||
        !draft.items.some(
          (i) =>
            i.id === w.itemId &&
            !i.excluded &&
            !i.inactiveSource &&
            Object.prototype.hasOwnProperty.call(i.overrides, w.field!)
        )
    )
  const warn = (message: string, itemId?: string) =>
    warnings.push({ message, ...(itemId ? { itemId } : {}), blocking: true })
  const show = showValues(draft)
  if (!draft.settings.showApproved) warn('Review and approve the show details.')
  if (!show.title.trim() || !show.venue.trim() || !Number.isFinite(Date.parse(show.startsAt)))
    warn('Show title, venue, and date/time are required.')
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: show.timeZone })
  } catch {
    warn('Choose a valid IANA time zone.')
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(draft.branding.accent))
    warn('Accent must be a six-digit hex color.')
  if (draft.items.length > 80) warn('Programs support at most 80 items.')
  if (new Set(draft.items.map((i) => i.id)).size !== draft.items.length)
    warn('Item IDs must be unique.')
  for (const item of draft.items.filter((i) => !i.excluded && !i.inactiveSource)) {
    const values = effectiveValues(item)
    if (!values.title?.trim() || !item.approvedFields.includes('title'))
      warn('Enter and approve a public title.', item.id)
    if (
      !values.pieceType ||
      !PIECE_TYPES.includes(values.pieceType) ||
      !item.approvedFields.includes('pieceType')
    )
      warn('Choose and approve a piece type.', item.id)
    if (
      values.awardEligible &&
      (!item.approvedFields.includes('awardEligible') || !draft.settings.votingEnabled)
    )
      warn('Approve award eligibility and enable voting, or mark this item ineligible.', item.id)
    for (const field of ['title', 'creator', 'description', 'credits', 'biography'] as const)
      if (item.approvedFields.includes(field) && containsPrivateData(values[field] ?? ''))
        warn('Public content must not contain contact information or private file links.', item.id)
    if (
      values.durationSeconds !== undefined &&
      values.durationSeconds !== null &&
      (!Number.isFinite(values.durationSeconds) || values.durationSeconds < 0)
    )
      warn('Duration must be a nonnegative number of seconds.', item.id)
  }
  return warnings
}
export function publicSnapshot(draft: ProgramDraft, version: number): PublicProgram {
  return {
    schemaVersion: 1,
    id: draft.id,
    slug: draft.slug,
    productionId: draft.productionId,
    eventId: draft.eventId,
    version,
    show: showValues(draft),
    branding: {
      ...draft.branding,
      ...(draft.branding.hero ? { hero: { ...draft.branding.hero } } : {}),
      ...(draft.branding.logo ? { logo: { ...draft.branding.logo } } : {}),
    },
    votingEnabled: draft.settings.votingEnabled,
    items: draft.items
      .filter((i) => !i.excluded && !i.inactiveSource)
      .map((item) => {
        const values = effectiveValues(item)
        const result: PieceValues & { id: string } = { id: item.id }
        for (const field of ITEM_FIELDS)
          if (item.approvedFields.includes(field) && values[field] !== undefined)
            Object.assign(result, {
              [field]: field === 'image' && values.image ? { ...values.image } : values[field],
            })
        return result
      }),
  }
}
export function awardRoster(program: PublicProgram): Array<{ id: string; title: string }> {
  return program.votingEnabled
    ? program.items
        .filter((i) => i.awardEligible === true)
        .map((i) => ({ id: i.id, title: i.title ?? '' }))
    : []
}
